import { useContext, useMemo } from 'react';

import cn from 'classnames';
import { Column } from 'react-table';

import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { TCombo } from '../types';
import s from './useGetColumns.module.scss';

export const useGetColumns = (): Column<TCombo>[] => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const size = useWindowResize();

  return useMemo(() => {
    const columns: Column<TCombo>[] = [
      {
        Header: t('title'),
        accessor: 'title',
        Cell: ({ row }: { row: { original: TCombo } }) => {
          const { title, host_group } = row.original;

          if (size.width > 960) {
            return title;
          } else {
            return (
              <div className={s.mobile_block}>
                <span className={s.title}>{title}</span>
                {host_group && (
                  <span className={cn(s.discount, host_group && s.active)}>
                    {host_group?.title}
                  </span>
                )}
              </div>
            );
          }
        },
        width: 329,
      },
    ];
    if (size.width > 960) {
      columns.push({
        Header: t('combo.group'),
        id: 'host_group',
        accessor: ({ host_group }) => host_group?.title || false,
        Cell: ({ value }: { value: string | false }) => {
          return (
            <div
              className={cn(s.column_category_block, value && s.active_block)}
            >
              <span className={cn(s.text, value && s.active)}>
                {value ? value : t('hosts.allGroups')}
              </span>
            </div>
          );
        },
      });
    }
    columns.push({
      Header: t('amount'),
      accessor: 'quantity',
      Cell: ({ value }: { value: number }) => (
        <div>{value < 0 ? '∞' : `${value.toLocaleString()} ${t('pcs')}`}</div>
      ),
    });

    if (size.width > 960) {
      columns.push({
        Header: t('combo.price.base'),
        Cell: ({ value }: { value: number }) => (
          <span className={s.base}>
            {value.toLocaleString()} {currency.sign}
          </span>
        ),
        accessor: 'base_price',
        id: 'base_price',
      });
    }

    columns.push({
      Header: t('combo.price'),
      id: 'price',
      Cell: ({ value, row }: { value: number; row: { original: TCombo } }) => {
        if (size.width > 960) {
          return `${value.toLocaleString()} ${currency.sign}`;
        } else {
          return (
            <div className={s.price_block}>
              <span>
                {value} {currency.sign}
              </span>
              <span className={s.base_price}>
                {row?.original?.base_price} {currency.sign}
              </span>
            </div>
          );
        }
      },
      width: 200,
      accessor: 'price',
    });

    return columns;
  }, [t, currency.sign, size]);
};
